import type { Conditions } from '@risksmart-app/form-configuration/src/field-types/types';
import type { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FormEditorPreviewData } from 'src/blocks/form-editor-dialog';
import type { SectionEditorValues } from 'src/blocks/form-editor-dialog/config';

import type {
  CustomisableFormDataContextState,
  TypedFormFieldConfiguration,
} from './customisable-form-data/CustomisableFormDataContext';

/**
 * Converts a single editor conditional logic rule set into the Conditions type
 * used by the form customisation system.
 */
export const convertEditorRulesToConditions = (
  rules: { ifField: string; values: string[] }[] | undefined,
  enabled: boolean
): Conditions | null => {
  if (!enabled || !rules || rules.length === 0) {
    return null;
  }

  const tokenGroups: Conditions['tokenGroups'] = rules.map((rule) => {
    if (rule.values.length === 1) {
      return {
        value: rule.values[0] as Required<unknown>,
        propertyKey: rule.ifField,
        operator: '=' as const,
      };
    }

    return {
      operation: 'or' as const,
      tokens: rule.values.map((value) => ({
        value: value as Required<unknown>,
        propertyKey: rule.ifField,
        operator: '=' as const,
      })),
    };
  });

  return {
    operation: 'and' as const,
    tokens: [],
    tokenGroups,
  };
};

/**
 * Builds a formFieldConfigurations entry from editor state, merging with
 * existing DB data where the editor hasn't made changes.
 */
const buildFieldConfiguration = (
  fieldId: string,
  data: FormEditorPreviewData,
  parentType: Parent_Type_Enum,
  existing: TypedFormFieldConfiguration | undefined
): TypedFormFieldConfiguration => {
  const field = data.fields[fieldId];
  const config = data.fieldConfigs[fieldId];

  if (!config) {
    // No editor config — use existing DB data or sensible defaults
    return (
      existing ?? {
        FieldId: fieldId,
        FormConfigurationParentType: parentType,
        Hidden: false,
        Required: field?.required ?? false,
        ReadOnly: field?.readOnly ?? false,
        DefaultValue: null,
        Label: field?.name ?? null,
        Description: null,
        Conditions: null,
      }
    );
  }

  return {
    FieldId: fieldId,
    FormConfigurationParentType: parentType,
    Hidden: false,
    Required: config.required ?? field?.required ?? false,
    ReadOnly: config.readOnly ?? field?.readOnly ?? false,
    DefaultValue: existing?.DefaultValue ?? null,
    Label: config.fieldName ?? field?.name ?? null,
    Description: config.guidanceEnabled ? (config.guidance ?? null) : null,
    Conditions: convertEditorRulesToConditions(
      config.conditionalLogicRules,
      config.conditionalLogicEnabled
    ),
  };
};

/**
 * Builds a formSections entry from editor state, merging with existing DB data.
 */
const buildSectionConfiguration = ({
  sectionId,
  index,
  data,
  parentType,
  sectionConfig,
}: {
  sectionId: string;
  index: number;
  data: FormEditorPreviewData;
  parentType: Parent_Type_Enum;
  sectionConfig: SectionEditorValues | undefined;
}) => {
  const fieldIds = data.containers[sectionId] ?? [];

  return {
    FormConfigurationParentType: parentType,
    SectionId: sectionId,
    Label: data.sectionNames[sectionId] ?? sectionId,
    Description: sectionConfig?.description ?? null,
    Conditions: sectionConfig
      ? convertEditorRulesToConditions(
          sectionConfig.conditionalLogicRules,
          sectionConfig.conditionalLogicEnabled
        )
      : null,
    fields: fieldIds.map((id) => ({ FieldId: id })),
    ordering: { Position: index },
  };
};

/**
 * Converts form editor dialog state into the CustomisableFormDataContextState
 * used by the form rendering pipeline.
 *
 * Merges editor changes with existing DB data — fields/sections the user hasn't
 * modified in the editor retain their original configuration.
 */
export const convertEditorStateToFormData = (
  data: FormEditorPreviewData,
  parentType: Parent_Type_Enum,
  existingContext: CustomisableFormDataContextState
): CustomisableFormDataContextState => {
  const existingFieldConfigMap = new Map(
    (existingContext.formFieldConfigurations ?? []).map((fc) => [
      fc.FieldId,
      fc,
    ])
  );

  // Collect all field IDs across all sections
  const allFieldIds = data.containerOrder.flatMap(
    (id) => data.containers[id] ?? []
  );

  const formFieldConfigurations = allFieldIds.map((fieldId) =>
    buildFieldConfiguration(
      fieldId,
      data,
      parentType,
      existingFieldConfigMap.get(fieldId)
    )
  );

  const formFieldOrdering = allFieldIds.map((fieldId, index) => ({
    FieldId: fieldId,
    Position: index,
    FormConfigurationParentType: parentType,
  }));

  const formSections = data.containerOrder.map((sectionId, index) =>
    buildSectionConfiguration({
      sectionId,
      index,
      data,
      parentType,
      sectionConfig: data.sectionConfigs[sectionId],
    })
  );

  return {
    formFieldConfigurations,
    formFieldOrdering,
    formSections,
    customAttributeSchema: existingContext.customAttributeSchema,
  };
};
